/* eslint-disable */
/* @ts-nocheck */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, XmarkIcon } from '@revfluence/fresh-icons/solid/esm';
import { uniqBy } from 'lodash';
import { TrashIcon } from '@revfluence/fresh-icons/regular/esm';
import { snakeToInitCap } from '@frontend/applications/AffiliatesApp/utils';
import { format } from 'date-fns';
import { Input } from '@/shadcn/components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/shadcn/components/ui/select';
import { ComparisonOperator, Condition, ConditionOption } from './BoostTypes';
import { Calendar } from '@/shadcn/components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/shadcn/components/ui/popover';
import { Button } from '@/shadcn/components/ui/button';
import { cn } from '@/shadcn/lib/utils';

interface AdvancedSelectionCardFormProps {
  initialCondition: Condition | null;
  index: number | null;
  onSave: (index: number | null, condition: Condition) => void;
  onCancel: () => void;
  conditionOptions: ConditionOption[];
  mode: 'add' | 'edit' | 'display';
  onDelete?: () => void;
}

const STATIC_OPERATORS = [
  'EQUALS',
  'CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
  'GREATER_THAN',
  'LESS_THAN',
  'MULTIPLE_OF',
];

// Operator map by attribute type
const OPERATOR_MAP: Record<string, string[]> = {
  NUMBER: [
    'EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_EQUALS',
    'LESS_THAN',
    'LESS_THAN_EQUALS',
    'MULTIPLE_OF',
  ],
  STRING: [
    'EQUALS',
    'CONTAINS',
    'STARTS_WITH',
    'ENDS_WITH',
  ],
  EMAIL: ['EQUALS', 'CONTAINS', 'STARTS_WITH', 'ENDS_WITH'],
  BOOLEAN: ['EQUALS'],
  DATE: [
    'EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_EQUALS',
    'LESS_THAN',
    'LESS_THAN_EQUALS',
  ],
};

function getUniqueGroups(options: ConditionOption[]) {
  return ['All', ...uniqBy(options, 'group').map((opt) => opt.group)];
}

const AdvancedSelectionCardForm: React.FC<AdvancedSelectionCardFormProps> = ({
  initialCondition,
  index,
  onSave,
  onCancel,
  conditionOptions,
  mode,
  onDelete,
}) => {
  const groups = getUniqueGroups(conditionOptions);
  const [group, setGroup] = useState(groups[0]);
  const [attribute, setAttribute] = useState<string | null>(null);
  const [operator, setOperator] = useState<string | null>(null);
  const [value, setValue] = useState<any>(null);
  const setGroupOnChange = useCallback((group: string) => {
    setGroup(group);
    setAttribute(null);
    setValue(null);
    setOperator(null);
  }, [setGroup, setAttribute, setValue, setOperator]);

  const setAttributeOnChange = useCallback((attribute: string) => {
    setAttribute(attribute);
    setValue(null);
    setOperator(null);
  }, [setAttribute, setValue, setOperator]);

  const filteredAttributes = useMemo(() => (group === 'All' ? conditionOptions : conditionOptions.filter((opt) => opt.group === group)), [group, conditionOptions]);

  const attributeType = useMemo(() => {
    const attr = conditionOptions.find((opt) => opt.id === attribute);
    return attr?.attributeType || 'STRING';
  }, [attribute, conditionOptions]);

  const validOperators = useMemo(() => OPERATOR_MAP[attributeType] || STATIC_OPERATORS, [attributeType]);
  const didInit = useRef(false);

  useEffect(() => {
    if (mode === 'edit' && !didInit.current) {
      const groupVal = initialCondition.group && groups.includes(initialCondition.group)
        ? initialCondition.group
        : groups[0];
      setGroup(groupVal);
      setAttribute(
        initialCondition.attribute && filteredAttributes.some((attr) => attr.id === initialCondition.attribute)
          ? initialCondition.attribute
          : filteredAttributes[0]?.id || ''
      );
      console.log({
        operator: initialCondition.op,
        validOperators,
      })
      setOperator(
        initialCondition.op || STATIC_OPERATORS[0]
      );
      setValue(initialCondition.value ?? '');
      didInit.current = true;
    } else if (mode === 'add' && !didInit.current) {
      setGroupOnChange(groups[0]);
      didInit.current = true;
    } else {
      didInit.current = false;
    }
    // Only run on mount
  }, [mode, initialCondition, validOperators]);

  function handleSave() {
    let parsedValue: any = value;
    if (attributeType === 'NUMBER') {
      parsedValue = value === '' ? '' : Number(value);
    } else if (attributeType === 'BOOLEAN') {
      parsedValue = String(value) === 'true';
    } else if (attributeType === 'DATE') {
      parsedValue = value ? new Date(value) : '';
    }
    onSave(index, {
      type: 'COMPARISON',
      op: operator as ComparisonOperator,
      value: parsedValue,
      variableId: 'TRIGGER_MEMBER',
      attribute,
      group,
      locked: false,
    });
  }

  return (
    <div className="bg-grey-1 border border-grey-2 rounded-xl p-4 mb-2 flex flex-col gap-2 relative shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Select value={group} onValueChange={setGroupOnChange}>
          <SelectTrigger className="w-[180px] h-8 text-sm border-gray-300">
            <SelectValue placeholder="Group" />
          </SelectTrigger>
          <SelectContent>
            {groups.map((g) => (
              <SelectItem key={g} value={g}>{snakeToInitCap(g)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-row gap-2 items-center">
        <Select value={attribute} onValueChange={setAttributeOnChange}>
          <SelectTrigger className="w-[180px] h-8 text-sm border-gray-300">
            <SelectValue placeholder="Attribute" />
          </SelectTrigger>
          <SelectContent>
            {filteredAttributes.map((attr) => (
              <SelectItem key={attr.id} value={attr.id}>{attr.displayName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={operator} onValueChange={setOperator}>
          <SelectTrigger className="w-[120px] h-8 text-sm border-gray-300">
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            {validOperators.map((op) => (
              <SelectItem key={op} value={op}>{snakeToInitCap(op)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Value input by type */}
        {attributeType === 'NUMBER' && (
          <Input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-[140px] h-8 text-sm border-gray-300"
            placeholder="Value"
          />
        )}
        {attributeType === 'EMAIL' && (
          <Input
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-[140px] h-8 text-sm border-gray-300"
            placeholder="Email"
          />
        )}
        {attributeType === 'BOOLEAN' && (
          <Select value={String(value)} onValueChange={(v) => setValue(v)}>
            <SelectTrigger className="w-[100px] h-8 text-sm border-gray-300">
              <SelectValue placeholder="Value" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        )}
        {attributeType === 'DATE' && (
          <DateValuePicker value={value} onChange={setValue} />
        )}
        {attributeType !== 'NUMBER' && attributeType !== 'EMAIL' && attributeType !== 'BOOLEAN' && attributeType !== 'DATE' && (
          <Input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-[140px] h-8 text-sm border-gray-300"
            placeholder="Value"
          />
        )}
      </div>
      {/* Action row below fields */}
      <div className="flex flex-row justify-between items-center mt-3 border-t border-gray-100 pt-3 w-full">
        <div className="w-[10%] flex justify-end gap-2">
          <Button
            size="smallIcon"
            type="button"
            onClick={handleSave}
            disabled={!attribute || !operator || !value}
            className={cn(
              'cursor-pointer transition-all duration-300 ease-in-out',
              !attribute || !operator ? 'opacity-50 cursor-not-allowed' : ''
            )}
          >
            <CheckIcon />
          </Button>
          <Button
            variant="outline"
            size="smallIcon"
            type="button"
            onClick={onCancel}
            className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5"
          >
            <XmarkIcon />
          </Button>
        </div>
        {mode === 'edit' && (
          <Button
            size="smallIcon"
            type="button"
            onClick={onDelete}
            className="ml-1 border-red-5"
            variant="outline"
          >
            <TrashIcon className="w-5 h-5 text-red-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

function DateValuePicker({ value, onChange }: { value: Date; onChange: (v: Date) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-[140px] h-8 text-left font-normal',
            !value && 'text-muted-foreground',
          )}
        >
          {value ? format(new Date(value), 'MMM dd, yyyy') : 'Pick a date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-auto pointer-events-auto select-text">
        <Calendar
          mode="single"
          selected={value ? new Date(value) : undefined}
          onSelect={(date) => {
            setOpen(false);
            onChange(date);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default AdvancedSelectionCardForm;
